export type ApiQueryParams = Record<string, unknown>;

/** Ensures a path starts with `/` and ends with `/` (Django/DRF convention). */
export function apiPath(path: string): string {
  const p = path.startsWith('/') ? path : `/${path}`;
  return p.endsWith('/') ? p : `${p}/`;
}

/**
 * Normalizes API URLs for shared Redis cache:
 * - force HTTPS
 * - trailing slash on pathname (before query)
 * - literal `=` in path segments (not `%3D`)
 */
export function normalizeApiUrl(url: string): string {
  if (!url) return url;

  const https = url.replace(/^http:\/\//i, 'https://');

  try {
    const parsed = new URL(https);
    parsed.pathname = parsed.pathname.replace(/%3D/gi, '=');
    if (!parsed.pathname.endsWith('/')) {
      parsed.pathname = `${parsed.pathname}/`;
    }
    return parsed.toString();
  } catch {
    const qIndex = https.indexOf('?');
    const pathPart = (qIndex >= 0 ? https.slice(0, qIndex) : https).replace(/%3D/gi, '=');
    const query = qIndex >= 0 ? https.slice(qIndex) : '';
    return `${apiPath(pathPart)}${query}`;
  }
}

/** Stable query string (sorted keys) for reproducible cache keys. */
export function buildQueryString(params: ApiQueryParams): string {
  const parts = Object.entries(params)
    .filter((entry): entry is [string, string | number | boolean] => {
      const value = entry[1];
      return value !== undefined && value !== null && value !== '';
    })
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => {
      if (typeof value === 'boolean') {
        return `${key}=${value ? 'true' : 'false'}`;
      }
      return `${key}=${encodeURIComponent(String(value))}`;
    });

  return parts.length ? `?${parts.join('&')}` : '';
}

/** Builds a canonical POST URL for `/silo_profiles/silo_profile_history/` (query flags only). */
export function siloProfileHistoryPostUrl(
  apiBase: string,
  extraQuery: ApiQueryParams = {},
): string {
  const base = apiBase.replace(/\/$/, '');
  return normalizeApiUrl(`${base}${apiPath('/silo_profiles/silo_profile_history/')}${buildQueryString(extraQuery)}`);
}

/** Sensor home report path with literal `=` (not `%3D`). */
export function smartfeedHomeReportUrl(apiBase: string, sensorPk: number): string {
  const base = apiBase.replace(/\/$/, '');
  return normalizeApiUrl(`${base}/sensors/report/data/home/smartfeed/smartfeed_pk=${sensorPk}/`);
}
